import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ResultadoBuscaNome } from '../types';

interface ModalBuscarNomeProps {
  eventoId: number;
  // Recebe o código do resultado e segue pelo MESMO caminho do "Digitar código".
  aoDarEntrada: (codigo: string) => void;
  aoFechar: () => void;
}

// "14:05" no horário de Brasília.
function horaBrasilia(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

function contarLetras(t: string): number {
  return (t.match(/[A-Za-zÀ-ÿ]/g) || []).length;
}

function contarDigitos(t: string): number {
  return (t.match(/\d/g) || []).length;
}

// Selo da situação do ingresso.
function SeloSituacao({ situacao }: { situacao: string }) {
  const s = (situacao || '').toLowerCase();
  let classe = 'bg-gray-100 text-gray-700';
  let rotulo = situacao || '—';
  if (s === 'pago') {
    classe = 'bg-green-100 text-green-700';
    rotulo = 'Pago';
  } else if (s === 'cortesia') {
    classe = 'bg-blue-100 text-blue-700';
    rotulo = 'Cortesia';
  } else if (s === 'pix_nao_pago') {
    classe = 'bg-amber-100 text-amber-700';
    rotulo = 'Pix não pago';
  }
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${classe}`}
    >
      {rotulo}
    </span>
  );
}

export default function ModalBuscarNome({ eventoId, aoDarEntrada, aoFechar }: ModalBuscarNomeProps) {
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<ResultadoBuscaNome[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const [conferindo, setConferindo] = useState<ResultadoBuscaNome | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Foco automático no campo.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Busca enquanto digita (espera 400 ms parado), só a partir de 3 letras ou 4 números.
  useEffect(() => {
    const t = texto.trim();
    if (contarLetras(t) < 3 && contarDigitos(t) < 4) {
      setResultados([]);
      setErro(null);
      setBuscou(false);
      return;
    }

    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data, error } = await supabase.rpc('portaria_buscar', {
          p_evento: eventoId,
          p_texto: t,
        });
        if (error) throw error;
        const r = data as { ok?: boolean; resultados?: ResultadoBuscaNome[]; erro?: string } | null;
        if (r?.ok) {
          setResultados(r.resultados ?? []);
          setErro(null);
        } else {
          setResultados([]);
          setErro(r?.erro || 'Não conseguimos buscar agora.');
        }
      } catch {
        setResultados([]);
        setErro('Não conseguimos buscar agora. Tente de novo.');
      } finally {
        setBuscando(false);
        setBuscou(true);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [texto, eventoId]);

  // Passo de conferência — dentro do mesmo modal.
  if (conferindo) {
    return (
      <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 sm:items-center sm:p-6">
        <div className="animate-modal-pop w-full rounded-t-lg bg-white sm:max-w-md sm:rounded-lg">
          <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
            <h3 className="text-lg font-medium text-foreground-950">Conferir documento</h3>
            <button
              type="button"
              onClick={aoFechar}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-500 hover:bg-background-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="px-5 py-5">
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-amber-800">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-id-card-line text-lg" />
              </span>
              <span className="text-base font-bold">Confira o documento com foto</span>
            </div>

            <p className="mt-5 text-2xl font-bold text-foreground-950">{conferindo.nome}</p>
            {conferindo.cpf_final && (
              <p className="mt-1 text-base text-secondary-600">CPF {conferindo.cpf_final}</p>
            )}
            <div className="mt-3">
              <SeloSituacao situacao={conferindo.situacao} />
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-secondary-100 px-5 py-4">
            <button
              type="button"
              onClick={() => aoDarEntrada(conferindo.codigo)}
              className="w-full cursor-pointer whitespace-nowrap rounded-md bg-green-600 px-4 py-3 text-base font-bold text-white transition-colors hover:bg-green-700"
            >
              Documento confere — dar entrada
            </button>
            <button
              type="button"
              onClick={() => setConferindo(null)}
              className="w-full cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 sm:items-center sm:p-6">
      <div className="animate-modal-pop flex max-h-[85vh] w-full flex-col rounded-t-lg bg-white sm:max-w-md sm:rounded-lg">
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
          <h3 className="text-lg font-medium text-foreground-950">Buscar por nome</h3>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-500 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-5 pt-4">
          <input
            ref={inputRef}
            type="text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Nome ou CPF"
            autoComplete="off"
            autoCorrect="off"
            className="w-full rounded-md border border-secondary-300 bg-white px-4 py-3 text-base text-foreground-950 placeholder:text-secondary-300 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
          {erro && <p className="mt-2 text-sm text-secondary-500">{erro}</p>}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {buscando && <p className="text-sm text-secondary-500">Buscando…</p>}

          {!buscando && buscou && resultados.length === 0 && !erro && (
            <p className="py-6 text-center text-sm text-secondary-500">
              Ninguém com esse nome neste evento.
            </p>
          )}

          <div className="space-y-2.5">
            {resultados.map((r) => {
              const jaEntrou = !!r.entrou_em;
              return (
                <button
                  key={r.codigo}
                  type="button"
                  disabled={jaEntrou}
                  onClick={() => setConferindo(r)}
                  className={`w-full rounded-lg border p-4 text-left transition-colors ${
                    jaEntrou
                      ? 'cursor-default border-secondary-100 bg-background-50 opacity-70'
                      : 'cursor-pointer border-secondary-200 bg-white hover:border-primary-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0 text-lg font-bold text-foreground-950">{r.nome}</span>
                    <SeloSituacao situacao={r.situacao} />
                  </div>
                  {r.cpf_final && (
                    <p className="mt-1 text-sm text-secondary-500">CPF {r.cpf_final}</p>
                  )}
                  {r.comprado_por && r.comprado_por !== r.nome && (
                    <p className="mt-1 text-sm text-secondary-500">Comprou: {r.comprado_por}</p>
                  )}
                  {jaEntrou && (
                    <p className="mt-1 text-sm font-semibold text-red-600">
                      Já entrou às {horaBrasilia(r.entrou_em)}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}